/**
 * API-Client.
 *
 * Kümmert sich um genau zwei Dinge, die man sonst überall wiederholt und irgendwo
 * vergisst: das CSRF-Token in den Header schreiben und Serverfehler in etwas
 * verwandeln, das man einem Menschen zeigen kann.
 */

const CSRF_COOKIE = '__Host-fh_csrf'
const CSRF_HEADER = 'X-FH-CSRF'

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Sitzung abgelaufen oder widerrufen — die App muss zur Anmeldung. */
  get needsLogin(): boolean {
    return this.status === 401
  }
}

/** Wird auch vom SOS-Weg gebraucht, der bewusst an `request()` vorbeigeht. */
export function csrfToken(): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`))
  return match ? decodeURIComponent(match[1]!) : ''
}

interface ErrorBody {
  error?: { code?: string; message?: string; requestId?: string }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' }
  if (body !== undefined) headers['content-type'] = 'application/json'
  if (method !== 'GET' && method !== 'HEAD') headers[CSRF_HEADER] = csrfToken()

  let res: Response
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? null : JSON.stringify(body),
    })
  } catch {
    // Netzwerkfehler sind für den Nutzer dasselbe wie „offline" — und genau so
    // soll es in der Oberfläche stehen, nicht als „TypeError: Failed to fetch".
    throw new ApiError('offline', 'Keine Verbindung zum Server.', 0)
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const data: unknown = text ? JSON.parse(text) : null

  if (!res.ok) {
    const err = (data as ErrorBody)?.error
    throw new ApiError(
      err?.code ?? 'internal',
      err?.message ?? 'Da ist etwas schiefgelaufen.',
      res.status,
      err?.requestId,
    )
  }

  return data as T
}

/**
 * Rohe Bytes senden — nur für Datei-Blöcke.
 *
 * Eigener Pfad statt eines Sonderfalls in `request`: Dort wird jeder Body
 * durch `JSON.stringify` geschickt, was einen ArrayBuffer stillschweigend zu
 * `{}` machen würde. Ein Upload, der leere Blöcke sendet und dabei „ok" meldet,
 * ist schlimmer als einer, der scheitert.
 */
async function requestRaw<T>(
  method: string,
  path: string,
  body: ArrayBuffer,
  opts: { signal?: AbortSignal } = {},
): Promise<T> {
  let res: Response
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      headers: {
        accept: 'application/json',
        'content-type': 'application/octet-stream',
        [CSRF_HEADER]: csrfToken(),
      },
      credentials: 'same-origin',
      body,
      ...(opts.signal ? { signal: opts.signal } : {}),
    })
  } catch {
    throw new ApiError('offline', 'Keine Verbindung zum Server.', 0)
  }

  const text = await res.text()
  const data: unknown = text ? JSON.parse(text) : null
  if (!res.ok) {
    const err = (data as ErrorBody)?.error
    throw new ApiError(
      err?.code ?? 'internal',
      err?.message ?? 'Da ist etwas schiefgelaufen.',
      res.status,
      err?.requestId,
    )
  }
  return data as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown = {}) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown = {}) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  putRaw: <T>(path: string, body: ArrayBuffer, opts?: { signal?: AbortSignal }) =>
    requestRaw<T>('PUT', path, body, opts),
}

// --- Typen der Antworten -----------------------------------------------------

export interface Me {
  personId: string
  familyId: string
  role: string
  displayName: string
  permissions: string[]
  prefs: { scale: string; mode: string; perf: string }
  recoveryCodesLeft: number
}

export interface Device {
  id: string
  personId: string
  name: string
  platform: string | null
  lastSeenAt: string | null
  blockedAt: string | null
  verifiedAt: string | null
  activeSessions: number
  isCurrent: boolean
}

/**
 * Gerätename für die Anmeldung. Bewusst grob: „Chrome auf Windows" ist für die
 * Geräteliste verständlich, ohne einen Fingerabdruck des Browsers zu bauen.
 */
export function deviceHint(): { name: string; platform: string } {
  const ua = navigator.userAgent
  const browser =
    /Edg\//.test(ua) ? 'Edge' :
    /OPR\//.test(ua) ? 'Opera' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Safari\//.test(ua) ? 'Safari' : 'Browser'
  const os =
    /Android/.test(ua) ? 'Android' :
    /iPhone|iPad/.test(ua) ? 'iPhone/iPad' :
    /Windows/.test(ua) ? 'Windows' :
    /Mac OS/.test(ua) ? 'Mac' :
    /Linux/.test(ua) ? 'Linux' : 'Gerät'
  return { name: `${browser} auf ${os}`, platform: os }
}
